import { SpaEntityConfigWizard } from '@common/components/SpaEntity/SpaEntityConfigWizard/SpaEntityConfigWizard';
import { SpaEntityConfigWizardProps } from '@common/components/SpaEntity/SpaEntityConfigWizard/SpaEntityConfigWizard.types';
import { SpaEntityFormSteps } from '@common/components/SpaEntity/SpaEntityFormSteps/SpaEntityFormSteps';
import {
  buildSpaEntityFormSchema,
  SpaEntityFormValues,
} from '@common/components/SpaEntity/SpaEntityForm.schema';
import { Routes } from '@common/constants';
import { EntityTypes } from '@common-types/Entities';
import {
  useGetSpaSettings,
  useGetSpaTreatment,
  useGetSpaTreatmentTimeslots,
} from '@features/spa/hooks/index';
import { basketSelectors } from '@store/basket/selectors';
import { yourJourneySelectors } from '@store/yourJourney/selectors';
import { useQueryClient } from '@tanstack/react-query';
import { parseDateToString } from '@utils/helpers';
import { getDaysOfTheWeek } from '@utils/date/daysOfTheWeek';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useEffect, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useRouter } from 'shared-routing';
import { SpaTreatmentFormStepEnum } from '../SpaTreatment.types';

export const SpaTreatmentConfigWizard: FC<SpaEntityConfigWizardProps> = ({
  spaEntity,
  language,
  basketDetails,
  bookingStep,
  existingGuests = [],
  signIn,
  handleBasketUpdate,
  property,
}) => {
  const router = useRouter();
  const queryClient = useQueryClient();

  const isSpinnerVisible = useSelector(basketSelectors.getIsBasketDetailsFetching);
  const journeyParams = useSelector(yourJourneySelectors.getParsedJourneyParams) as any;

  const { data: spaTreatmentWithAvailabilityDetail, isFetching } = useGetSpaTreatment(
    property.id,
    language,
    spaEntity.id,
    parseDateToString(journeyParams?.spaDate)
  );
  const isExtrasAvailable = !!(spaEntity?.extras?.length || spaEntity?.selectionGroups?.length);
  const [loginError, setLoginError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
  const [startDate, setStartDate] = useState(
    parseDateToString(journeyParams?.spaDate || journeyParams?.fromDate)
  );

  const currentSpaEntity = spaTreatmentWithAvailabilityDetail || spaEntity;

  const form = useForm<SpaEntityFormValues>({
    resolver: zodResolver(buildSpaEntityFormSchema(currentSpaEntity?.selectionGroups)),
    defaultValues: {
      guests: [],
    },
  });

  const selectedTimeSlot = useWatch({ control: form.control, name: 'arrivalTime' });

  const { data: timeslots, isFetching: isFetchingTimeslots } = useGetSpaTreatmentTimeslots(
    property.id,
    Number(spaEntity.id),
    startDate,
    language
  );

  const { data: spaSettings } = useGetSpaSettings(property.id);
  const { maxDaysAdvanceTreatments } = spaSettings || {};

  const daysToFetch = getDaysOfTheWeek(
    selectedTimeSlot?.startDateTime || parseDateToString(journeyParams?.spaDate)
  );

  useEffect(() => {
    if (bookingStep) {
      setIsModalOpen(true);
    } else {
      setIsModalOpen(false);
      form.reset();
    }
  }, [bookingStep, form]);

  const handleSignIn = async (data) => {
    try {
      await signIn(property.id, data);
      setIsSignInModalOpen(false);
    } catch (e) {
      setLoginError('invalidLogin');
    }
  };

  const handleSignInModalClose = () => {
    setIsSignInModalOpen(false);
  };

  const gotoEntityDetailPage = (): Promise<boolean> =>
    router.push({
      pathname: Routes.SPA_TREATMENT_DETAIL,
      query: {
        id: spaEntity.id,
      },
    });

  const goToStep = (bookingStep: string): Promise<boolean> =>
    router.push({
      pathname: Routes.SPA_TREATMENT_BOOKING,
      query: {
        id: spaEntity.id,
        bookingStep,
      },
    });

  const goToNextStep = async () => {
    if (bookingStep === SpaTreatmentFormStepEnum.AVAILABILITY && isExtrasAvailable) {
      goToStep(SpaTreatmentFormStepEnum.CUSTOMISE);
    }
  };

  const goToPreviousStep = () => {
    router.back();
  };

  const showConfirmation = () => {
    setIsConfirmationVisible(true);
  };

  const hideConfirmation = () => {
    setIsConfirmationVisible(false);
  };

  const finishWizardAction = async (data) => {
    await handleBasketUpdate(data);
    showConfirmation();
    await gotoEntityDetailPage();
    await queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey.includes('spaTreatment') && query.queryKey.includes('timeslots'),
    });
  };

  const hideArrivalTimes = spaSettings?.hideArrivalTimes || false;

  return (
    <FormProvider {...form}>
      <SpaEntityConfigWizard
        isConfirmationVisible={isConfirmationVisible}
        basketDetails={basketDetails}
        hideConfirmation={hideConfirmation}
        isSignInModalOpen={isSignInModalOpen}
        handleSignInModalClose={handleSignInModalClose}
        spaEntity={currentSpaEntity}
        availableTimeslots={timeslots}
        isSpaEntityFetching={isFetchingTimeslots || isFetching}
        fetchingDates={isFetchingTimeslots ? daysToFetch : []}
        handleSignIn={handleSignIn}
        loginError={loginError}
        isModalOpen={isModalOpen}
        isSpinnerVisible={!!(isSpinnerVisible || isFetchingTimeslots || isFetching)}
        goToPreviousStep={goToPreviousStep}
        gotoEntityDetailPage={gotoEntityDetailPage}
        SpaEntityFormSteps={SpaEntityFormSteps}
        bookingStep={bookingStep}
        language={language}
        existingGuests={existingGuests}
        maxDaysAdvance={maxDaysAdvanceTreatments || 0}
        isExtrasAvailable={isExtrasAvailable}
        goToNextStep={goToNextStep}
        setIsSignInModalOpen={setIsSignInModalOpen}
        finishWizardAction={finishWizardAction}
        selectedTimeSlot={selectedTimeSlot}
        goToStep={goToStep}
        SpaEntityFormStepEnum={SpaTreatmentFormStepEnum}
        setStartDate={setStartDate}
        modulesExcludingDisabled={property.modulesExcludingDisabled || {}}
        hideArrivalTimes={hideArrivalTimes}
        entityBookingRoute={Routes.SPA_TREATMENT_BOOKING}
        entityType={EntityTypes.TREATMENT}
        entityLabel="spa.spaTreatment"
      />
    </FormProvider>
  );
};
